/**
 * Spot REST API
 *
 * Access market data, manage accounts, and trade on Binance Spot.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
import {
    ConfigurationRestAPI,
    TimeUnit,
    RestApiResponse,
    sendRequest,
    type RequestArgs,
} from '@binance/common';
import type { ExchangeInfoResponse, ExecutionRulesResponse, TimeResponse } from '../types';

/**
 * GeneralApi - axios parameter creator
 */
const GeneralApiAxiosParamCreator = function (configuration: ConfigurationRestAPI) {
    return {
        /**
         * Current exchange trading rules and symbol information
         *
         * Weight(IP): 20
         *
         * Security Type: NONE
         *
         * Notes:
         **Data Source:** Memory
         *
         **Notes:**
         * If the value provided to `symbol` or `symbols` do not exist, the endpoint will throw an error saying the symbol is invalid.
         * All parameters are optional.
         * `permissions` can support single or multiple values (e.g. `SPOT`, `["MARGIN","LEVERAGED"]`). This cannot be used in combination with `symbol` or `symbols`.
         * If `permissions` parameter not provided, all symbols that have either `SPOT`, `MARGIN`, or `LEVERAGED` permission will be exposed.
         * To display symbols with any permission you need to specify them explicitly in `permissions`: (e.g. `["SPOT","MARGIN",...]`.). See Account and Symbol Permissions for the full list.
         *
         **Examples of Symbol Permissions Interpretation from the Response:**
         *
         * `[["A","B"]]` means you may place an order if your account has either permission "A" **or** permission "B".
         * `[["A"],["B"]]` means you can place an order if your account has permission "A" **and** permission "B".
         * `[["A"],["B","C"]]` means you can place an order if your account has permission "A" **and** permission "B" or permission "C". (Inclusive or is applied here, not exclusive or, so your account may have both permission "B" and permission "C".)
         *
         * @summary Exchange information
         * @param {string} [symbol] Example: curl -X GET "https://api.binance.com/api/v3/exchangeInfo?symbol=BNBBTC"
         * @param {Array<string>} [symbols] Examples: curl -X GET "https://api.binance.com/api/v3/exchangeInfo?symbols=%5B%22BNBBTC%22,%22BTCUSDT%22%5D" or curl -g -X GET 'https://api.binance.com/api/v3/exchangeInfo?symbols=["BTCUSDT","BNBBTC"]'
         * @param {Array<ExchangeInfoPermissionsEnum>} [permissions] Examples: curl -X GET "https://api.binance.com/api/v3/exchangeInfo?permissions=SPOT"
         *
         * curl -X GET "https://api.binance.com/api/v3/exchangeInfo?permissions=%5B%22MARGIN%22%2C%22LEVERAGED%22%5D"
         * or
         * curl -g -X GET 'https://api.binance.com/api/v3/exchangeInfo?permissions=["MARGIN","LEVERAGED"]'
         * @param {boolean} [showPermissionSets] Controls whether the content of the `permissionSets` field is populated or not.
         * @param {ExchangeInfoSymbolStatusEnum} [symbolStatus] Filters for symbols that have this `tradingStatus`. Cannot be used in combination with `symbols` or `symbol`.
         *
         * @throws {RequiredError}
         */
        exchangeInfo: async (
            symbol?: string,
            symbols?: Array<string>,
            permissions?: Array<ExchangeInfoPermissionsEnum>,
            showPermissionSets?: boolean,
            symbolStatus?: ExchangeInfoSymbolStatusEnum
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (symbols) {
                localVarQueryParameter['symbols'] = symbols;
            }
            if (permissions) {
                localVarQueryParameter['permissions'] = permissions;
            }
            if (showPermissionSets !== undefined && showPermissionSets !== null) {
                localVarQueryParameter['showPermissionSets'] = showPermissionSets;
            }
            if (symbolStatus !== undefined && symbolStatus !== null) {
                localVarQueryParameter['symbolStatus'] = symbolStatus;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/api/v3/exchangeInfo',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query execution rules for symbols.
         *
         * Weight: Parameter | Weight
         * --- | ---
         * `symbol` | 2
         * `symbols` | 2 for each `symbol`, capped at a max of 40
         * `symbolStatus` | 40
         * None | 40
         *
         * Security Type: NONE
         *
         * Notes:
         **Data Source:** Memory
         *
         **Note:**: No combination of multiple parameters is allowed.
         *
         * @summary Query Execution Rules
         * @param {string} [symbol] Query for specified symbol.
         * @param {Array<string>} [symbols] Query for multiple symbols.
         * @param {ExecutionRulesSymbolStatusEnum} [symbolStatus] Query for all symbols with the specified status.
         *
         * @throws {RequiredError}
         */
        executionRules: async (
            symbol?: string,
            symbols?: Array<string>,
            symbolStatus?: ExecutionRulesSymbolStatusEnum
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (symbol !== undefined && symbol !== null) {
                localVarQueryParameter['symbol'] = symbol;
            }
            if (symbols) {
                localVarQueryParameter['symbols'] = symbols;
            }
            if (symbolStatus !== undefined && symbolStatus !== null) {
                localVarQueryParameter['symbolStatus'] = symbolStatus;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/api/v3/executionRules',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Test connectivity to the Rest API.
         *
         * Weight(IP): 1
         *
         * Security Type: NONE
         *
         * @summary Test connectivity
         *
         * @throws {RequiredError}
         */
        ping: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/api/v3/ping',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Test connectivity to the Rest API and get the current server time.
         *
         * Weight(IP): 1
         *
         * Security Type: NONE
         *
         * @summary Check server time
         *
         * @throws {RequiredError}
         */
        time: async (): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/api/v3/time',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
    };
};

/**
 * GeneralApi - interface
 * @interface GeneralApi
 */
export interface GeneralApiInterface {
    /**
     * Current exchange trading rules and symbol information
     *
     * Weight(IP): 20
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Notes:**
     * If the value provided to `symbol` or `symbols` do not exist, the endpoint will throw an error saying the symbol is invalid.
     * All parameters are optional.
     * `permissions` can support single or multiple values (e.g. `SPOT`, `["MARGIN","LEVERAGED"]`). This cannot be used in combination with `symbol` or `symbols`.
     * If `permissions` parameter not provided, all symbols that have either `SPOT`, `MARGIN`, or `LEVERAGED` permission will be exposed.
     * To display symbols with any permission you need to specify them explicitly in `permissions`: (e.g. `["SPOT","MARGIN",...]`.). See Account and Symbol Permissions for the full list.
     *
     **Examples of Symbol Permissions Interpretation from the Response:**
     *
     * `[["A","B"]]` means you may place an order if your account has either permission "A" **or** permission "B".
     * `[["A"],["B"]]` means you can place an order if your account has permission "A" **and** permission "B".
     * `[["A"],["B","C"]]` means you can place an order if your account has permission "A" **and** permission "B" or permission "C". (Inclusive or is applied here, not exclusive or, so your account may have both permission "B" and permission "C".)
     *
     * @summary Exchange information
     * @param {ExchangeInfoRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof GeneralApiInterface
     */
    exchangeInfo(
        requestParameters?: ExchangeInfoRequest
    ): Promise<RestApiResponse<ExchangeInfoResponse>>;
    /**
     * Query execution rules for symbols.
     *
     * Weight: Parameter | Weight
     * --- | ---
     * `symbol` | 2
     * `symbols` | 2 for each `symbol`, capped at a max of 40
     * `symbolStatus` | 40
     * None | 40
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Note:**: No combination of multiple parameters is allowed.
     *
     * @summary Query Execution Rules
     * @param {ExecutionRulesRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof GeneralApiInterface
     */
    executionRules(
        requestParameters?: ExecutionRulesRequest
    ): Promise<RestApiResponse<ExecutionRulesResponse>>;
    /**
     * Test connectivity to the Rest API.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * @summary Test connectivity
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof GeneralApiInterface
     */
    ping(): Promise<RestApiResponse<void>>;
    /**
     * Test connectivity to the Rest API and get the current server time.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * @summary Check server time
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof GeneralApiInterface
     */
    time(): Promise<RestApiResponse<TimeResponse>>;
}

/**
 * Request parameters for exchangeInfo operation in GeneralApi.
 * @interface ExchangeInfoRequest
 */
export interface ExchangeInfoRequest {
    /**
     * Example: curl -X GET "https://api.binance.com/api/v3/exchangeInfo?symbol=BNBBTC"
     * @type {string}
     * @memberof GeneralApiExchangeInfo
     */
    readonly symbol?: string;

    /**
     * Examples: curl -X GET "https://api.binance.com/api/v3/exchangeInfo?symbols=%5B%22BNBBTC%22,%22BTCUSDT%22%5D" or curl -g -X GET 'https://api.binance.com/api/v3/exchangeInfo?symbols=["BTCUSDT","BNBBTC"]'
     * @type {Array<string>}
     * @memberof GeneralApiExchangeInfo
     */
    readonly symbols?: Array<string>;

    /**
     * Examples: curl -X GET "https://api.binance.com/api/v3/exchangeInfo?permissions=SPOT"
     *
     * curl -X GET "https://api.binance.com/api/v3/exchangeInfo?permissions=%5B%22MARGIN%22%2C%22LEVERAGED%22%5D"
     * or
     * curl -g -X GET 'https://api.binance.com/api/v3/exchangeInfo?permissions=["MARGIN","LEVERAGED"]'
     * @type {Array<'SPOT' | 'MARGIN' | 'LEVERAGED' | 'TRD_GRP_002' | 'TRD_GRP_003' | 'TRD_GRP_004' | 'TRD_GRP_005' | 'TRD_GRP_006' | 'TRD_GRP_007' | 'TRD_GRP_008' | 'TRD_GRP_009' | 'TRD_GRP_010' | 'TRD_GRP_011' | 'TRD_GRP_012' | 'TRD_GRP_013' | 'TRD_GRP_014' | 'TRD_GRP_015' | 'TRD_GRP_016' | 'TRD_GRP_017' | 'TRD_GRP_018' | 'TRD_GRP_019' | 'TRD_GRP_020' | 'TRD_GRP_021' | 'TRD_GRP_022' | 'TRD_GRP_023' | 'TRD_GRP_024' | 'TRD_GRP_025'>}
     * @memberof GeneralApiExchangeInfo
     */
    readonly permissions?: Array<ExchangeInfoPermissionsEnum>;

    /**
     * Controls whether the content of the `permissionSets` field is populated or not.
     * @type {boolean}
     * @memberof GeneralApiExchangeInfo
     */
    readonly showPermissionSets?: boolean;

    /**
     * Filters for symbols that have this `tradingStatus`. Cannot be used in combination with `symbols` or `symbol`.
     * @type {'TRADING' | 'HALT' | 'BREAK'}
     * @memberof GeneralApiExchangeInfo
     */
    readonly symbolStatus?: ExchangeInfoSymbolStatusEnum;
}

/**
 * Request parameters for executionRules operation in GeneralApi.
 * @interface ExecutionRulesRequest
 */
export interface ExecutionRulesRequest {
    /**
     * Query for specified symbol.
     * @type {string}
     * @memberof GeneralApiExecutionRules
     */
    readonly symbol?: string;

    /**
     * Query for multiple symbols.
     * @type {Array<string>}
     * @memberof GeneralApiExecutionRules
     */
    readonly symbols?: Array<string>;

    /**
     * Query for all symbols with the specified status.
     * @type {'TRADING' | 'HALT' | 'BREAK'}
     * @memberof GeneralApiExecutionRules
     */
    readonly symbolStatus?: ExecutionRulesSymbolStatusEnum;
}

/**
 * GeneralApi - object-oriented interface
 * @class GeneralApi
 */
export class GeneralApi implements GeneralApiInterface {
    private readonly configuration: ConfigurationRestAPI;
    private localVarAxiosParamCreator;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.localVarAxiosParamCreator = GeneralApiAxiosParamCreator(configuration);
    }

    /**
     * Current exchange trading rules and symbol information
     *
     * Weight(IP): 20
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Notes:**
     * If the value provided to `symbol` or `symbols` do not exist, the endpoint will throw an error saying the symbol is invalid.
     * All parameters are optional.
     * `permissions` can support single or multiple values (e.g. `SPOT`, `["MARGIN","LEVERAGED"]`). This cannot be used in combination with `symbol` or `symbols`.
     * If `permissions` parameter not provided, all symbols that have either `SPOT`, `MARGIN`, or `LEVERAGED` permission will be exposed.
     * To display symbols with any permission you need to specify them explicitly in `permissions`: (e.g. `["SPOT","MARGIN",...]`.). See Account and Symbol Permissions for the full list.
     *
     **Examples of Symbol Permissions Interpretation from the Response:**
     *
     * `[["A","B"]]` means you may place an order if your account has either permission "A" **or** permission "B".
     * `[["A"],["B"]]` means you can place an order if your account has permission "A" **and** permission "B".
     * `[["A"],["B","C"]]` means you can place an order if your account has permission "A" **and** permission "B" or permission "C". (Inclusive or is applied here, not exclusive or, so your account may have both permission "B" and permission "C".)
     *
     * @summary Exchange information
     * @param {ExchangeInfoRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<ExchangeInfoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof GeneralApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/general#exchange-info Binance API Documentation}
     */
    public async exchangeInfo(
        requestParameters: ExchangeInfoRequest = {}
    ): Promise<RestApiResponse<ExchangeInfoResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.exchangeInfo(
            requestParameters?.symbol,
            requestParameters?.symbols,
            requestParameters?.permissions,
            requestParameters?.showPermissionSets,
            requestParameters?.symbolStatus
        );
        return sendRequest<ExchangeInfoResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Query execution rules for symbols.
     *
     * Weight: Parameter | Weight
     * --- | ---
     * `symbol` | 2
     * `symbols` | 2 for each `symbol`, capped at a max of 40
     * `symbolStatus` | 40
     * None | 40
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Note:**: No combination of multiple parameters is allowed.
     *
     * @summary Query Execution Rules
     * @param {ExecutionRulesRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<ExecutionRulesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof GeneralApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/general#execution-rules Binance API Documentation}
     */
    public async executionRules(
        requestParameters: ExecutionRulesRequest = {}
    ): Promise<RestApiResponse<ExecutionRulesResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.executionRules(
            requestParameters?.symbol,
            requestParameters?.symbols,
            requestParameters?.symbolStatus
        );
        return sendRequest<ExecutionRulesResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Test connectivity to the Rest API.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * @summary Test connectivity
     * @returns {Promise<RestApiResponse<void>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof GeneralApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/general#ping Binance API Documentation}
     */
    public async ping(): Promise<RestApiResponse<void>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.ping();
        return sendRequest<void>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }

    /**
     * Test connectivity to the Rest API and get the current server time.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * @summary Check server time
     * @returns {Promise<RestApiResponse<TimeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof GeneralApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/general#time Binance API Documentation}
     */
    public async time(): Promise<RestApiResponse<TimeResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.time();
        return sendRequest<TimeResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: false }
        );
    }
}

export enum ExchangeInfoPermissionsEnum {
    SPOT = 'SPOT',
    MARGIN = 'MARGIN',
    LEVERAGED = 'LEVERAGED',
    TRD_GRP_002 = 'TRD_GRP_002',
    TRD_GRP_003 = 'TRD_GRP_003',
    TRD_GRP_004 = 'TRD_GRP_004',
    TRD_GRP_005 = 'TRD_GRP_005',
    TRD_GRP_006 = 'TRD_GRP_006',
    TRD_GRP_007 = 'TRD_GRP_007',
    TRD_GRP_008 = 'TRD_GRP_008',
    TRD_GRP_009 = 'TRD_GRP_009',
    TRD_GRP_010 = 'TRD_GRP_010',
    TRD_GRP_011 = 'TRD_GRP_011',
    TRD_GRP_012 = 'TRD_GRP_012',
    TRD_GRP_013 = 'TRD_GRP_013',
    TRD_GRP_014 = 'TRD_GRP_014',
    TRD_GRP_015 = 'TRD_GRP_015',
    TRD_GRP_016 = 'TRD_GRP_016',
    TRD_GRP_017 = 'TRD_GRP_017',
    TRD_GRP_018 = 'TRD_GRP_018',
    TRD_GRP_019 = 'TRD_GRP_019',
    TRD_GRP_020 = 'TRD_GRP_020',
    TRD_GRP_021 = 'TRD_GRP_021',
    TRD_GRP_022 = 'TRD_GRP_022',
    TRD_GRP_023 = 'TRD_GRP_023',
    TRD_GRP_024 = 'TRD_GRP_024',
    TRD_GRP_025 = 'TRD_GRP_025',
}

export enum ExchangeInfoSymbolStatusEnum {
    TRADING = 'TRADING',
    HALT = 'HALT',
    BREAK = 'BREAK',
}

export enum ExecutionRulesSymbolStatusEnum {
    TRADING = 'TRADING',
    HALT = 'HALT',
    BREAK = 'BREAK',
}
